import { MapDataNode, MapDataRoot, PerfData } from '../../../../app/maps/types'
import { isStockFastRefreshAvailable } from '../../../../app/shared/isStockFastRefreshAvailable'
import { FORMAT_PERCENT, formatNumber } from '../../../util'
import { getIndustries, groupByKey } from '../../portfolio/utils'
import { ETFHoldingsPollingIntervalInMs, NON_STOCK_INSTRUMENT_CATEGORIES } from './constants'
import {
  ETFHoldingsBreakdownApiResponse,
  ETFHoldingsBreakdownResponse,
  ETFHoldingsBreakdownRowData,
  ETFHoldingsStandardApiResponse,
  ETFHoldingsStandardResponse,
  ETFHoldingsStandardRowData,
} from './types'

export function getRounding() {
  return FinvizSettings.hasRedesignEnabled ? 'regular' : 'none'
}

export function getKeyFromStrings(...args: string[]) {
  return args.join('-')
}

export function splitStringByUpperCase(value: string) {
  return value.split(/(?=[A-Z])/).join(' ')
}

enum ETFHoldingsDataKey {
  Stocks = 'stocks',
  MoneyLike = 'moneyLike',
  Other = 'other',
}

const ETFHoldingsGroupLabel = {
  [ETFHoldingsDataKey.Stocks]: 'Stocks',
  [ETFHoldingsDataKey.MoneyLike]: 'Bonds, Cash & Other',
  [ETFHoldingsDataKey.Other]: 'Other',
}

export function getNormalizedStandardData(data?: ETFHoldingsStandardApiResponse) {
  if (!data?.rowData) return null

  const normalizedData: ETFHoldingsStandardRowData[] = data.rowData
    .map((holding) => ({
      ticker: holding.ticker ?? null,
      name: holding.name ?? 'Other',
      instrument: holding.instrument ?? 'Other',
      industry: splitStringByUpperCase(holding.industry ?? 'Other'),
      country: holding.country ?? 'Unknown',
      sector: splitStringByUpperCase(holding.sector ?? 'Other'),
      weight: holding.weight ?? 0,
      marketCap: holding.marketCap,
      absoluteWeight: Math.abs(holding.weight ?? 0),
    }))
    .sort((a, b) => b.absoluteWeight - a.absoluteWeight)

  return {
    rowData: normalizedData,
    etfHoldingsTotal: data.etfHoldingsTotal,
    etfTotalWeight: data.etfTotalWeight,
  }
}

export function getNormalizedBreakdownData(data?: ETFHoldingsBreakdownApiResponse) {
  if (!data?.rowData) return null

  const normalizedData: ETFHoldingsBreakdownRowData[] = data.rowData.map((instrumentGroup) => ({
    instrument: instrumentGroup.instrument ?? 'Other',
    data: instrumentGroup.data.map((sector) => ({
      sector: splitStringByUpperCase(sector.sector ?? 'Other'),
      absoluteWeight: sector.absoluteWeight,
    })),
    itemsToDisplay: 0,
    instrumentSum: instrumentGroup.instrumentSum,
  }))

  return { rowData: normalizedData, etfTotalWeight: data.etfTotalWeight }
}

type ETFHoldingsMapData = { [key in ETFHoldingsDataKey]: MapDataNode[] }

function createGroup(key: ETFHoldingsDataKey, data: ETFHoldingsMapData) {
  const groupName = ETFHoldingsGroupLabel[key] ?? key

  const children = getIndustries(groupByKey(data[key], 'description'))
  return {
    name: groupName,
    children,
  }
}

function getPerf(rowItem: ETFHoldingsStandardRowData, perfData?: PerfData) {
  if (rowItem.ticker) {
    return !perfData?.nodes ? undefined : (perfData?.nodes as { [key: string]: number })[rowItem.ticker.toUpperCase()]
  }
}

export function getStandardMapData({
  data,
  limitDataTo,
  perfData,
}: {
  data?: ETFHoldingsStandardResponse
  limitDataTo?: number
  perfData?: PerfData
}) {
  if (!data?.rowData) return { hasData: false }
  const dataToUse = limitDataTo ? data.rowData.slice(0, limitDataTo) : data.rowData

  const parsedData = dataToUse.reduce(
    (prev: ETFHoldingsMapData, current) => {
      const group = current.instrument === 'Stock' ? ETFHoldingsDataKey.Stocks : ETFHoldingsDataKey.MoneyLike
      const value = current.absoluteWeight

      const entry = {
        name: current.ticker ?? current.name?.slice(0, 10) ?? 'Unknown',
        value,
        perf: getPerf(current, perfData),
        description: getSector(current.instrument, current.sector),
        data: { group, nameOverride: !current.ticker ? current.name : undefined, ticker: current.ticker },
      } as MapDataNode

      if (value > 0) {
        return { ...prev, [group]: [...prev[group], entry] }
      }

      return prev
    },
    {
      [ETFHoldingsDataKey.Stocks]: [],
      [ETFHoldingsDataKey.MoneyLike]: [],
      [ETFHoldingsDataKey.Other]: [],
    }
  )

  if (limitDataTo !== undefined) {
    parsedData[ETFHoldingsDataKey.Other] = [
      {
        name: 'Other',
        value: data.etfTotalWeight - dataToUse.reduce((prev, curr) => prev + curr.absoluteWeight, 0),
        perf: undefined,
        description: 'Other',
        data: { group: 'Other', nameOverride: 'Other', ticker: 'Other' },
      } as MapDataNode,
    ]
  }

  return {
    hasData: Object.values(parsedData).some((arr) => arr.length > 0),
    data: {
      name: 'Root',
      children: Object.keys(parsedData)
        .map((key) => createGroup(key as ETFHoldingsDataKey, parsedData))
        .filter((category) => category.children.length),
    } as MapDataRoot,
  }
}

export function getBreakdownMapData(data: ETFHoldingsBreakdownResponse) {
  if (!data) return { hasData: false }
  const dataToUse = data.rowData

  return {
    hasData: dataToUse.some((item) => item.data.length > 0),
    data: {
      name: 'Breakdown',
      children: dataToUse.map((instrumentGroup) => ({
        name: instrumentGroup.instrument,
        children: instrumentGroup.data.map((sector) => ({
          name: sector.sector,
          value: (sector.absoluteWeight / data.etfTotalWeight) * 100,
          additional: formatNumber((sector.absoluteWeight / data.etfTotalWeight) * 100, FORMAT_PERCENT),
          data: {
            group: instrumentGroup.instrument,
          },
        })),
      })),
    } as unknown as MapDataRoot,
  }
}

export function getSector(instrument: string, sector: string) {
  if (instrument === 'Stock' || sector !== 'Other') return sector
  return NON_STOCK_INSTRUMENT_CATEGORIES[instrument as keyof typeof NON_STOCK_INSTRUMENT_CATEGORIES] ?? 'Other'
}

export function getReducedBreakdownData(data: ETFHoldingsBreakdownRowData[]) {
  const breakdownDataFull = data

  const MAX_SECTOR_SPACES = 22
  const instrumentsSum = breakdownDataFull.reduce((prev, curr) => (prev += curr.instrumentSum), 0)
  let instrumentSumOrderedIndexArray = [0]
  if (breakdownDataFull.length > 1) {
    instrumentSumOrderedIndexArray =
      breakdownDataFull[0].instrumentSum > breakdownDataFull[1].instrumentSum ? [0, 1] : [1, 0]
  }
  let availableSectorsSpaces = MAX_SECTOR_SPACES

  instrumentSumOrderedIndexArray.forEach((itemIndex, index) => {
    const sectorsInInstrument = breakdownDataFull[itemIndex].data.length
    let itemsToDisplay = 0
    const sectorsToShowByInstrumentWeight = Math.round(
      (index === 0 ? Math.min : Math.max)(
        breakdownDataFull[itemIndex].instrumentSum / instrumentsSum,
        index === 0 ? 0.9 : 0.1
      ) * MAX_SECTOR_SPACES
    )

    if (index === 0) {
      itemsToDisplay = Math.min(sectorsToShowByInstrumentWeight, sectorsInInstrument, availableSectorsSpaces)
    } else {
      itemsToDisplay = Math.min(
        Math.max(sectorsToShowByInstrumentWeight, sectorsInInstrument),
        availableSectorsSpaces,
        sectorsInInstrument
      )
    }

    breakdownDataFull[itemIndex].itemsToDisplay = itemsToDisplay

    availableSectorsSpaces -= itemsToDisplay

    if (availableSectorsSpaces && index === 1) {
      const firstInstrument = breakdownDataFull[instrumentSumOrderedIndexArray[0]]
      const sectorsInFirstInstrument = firstInstrument.data.length
      firstInstrument.itemsToDisplay = Math.min(
        availableSectorsSpaces + firstInstrument.itemsToDisplay,
        sectorsInFirstInstrument
      )
    }
  })

  const breakdownDataReduced = breakdownDataFull.map((instrument) => {
    const MAX_ITEMS =
      instrument.itemsToDisplay === instrument.data.length
        ? instrument.itemsToDisplay
        : Math.min(instrument.itemsToDisplay, Math.max(instrument.itemsToDisplay - 1, 1))
    return {
      instrument: instrument.instrument,
      data: [...instrument.data]
        .sort((a, b) => b.absoluteWeight - a.absoluteWeight)
        .reduce(
          (prev, curr, index) => {
            if (index < MAX_ITEMS) {
              return [...prev, curr]
            } else {
              const otherSectorIndex = prev.findIndex(({ sector }) => sector === 'Other')
              const indexForRemainingSectors = otherSectorIndex !== -1 ? otherSectorIndex : MAX_ITEMS
              prev[indexForRemainingSectors] = {
                sector: 'Other',
                absoluteWeight: (prev[indexForRemainingSectors]?.absoluteWeight ?? 0) + curr.absoluteWeight,
              }
              return prev
            }
          },
          [] as Array<{ sector: string; absoluteWeight: number }>
        )
        .sort((a, b) => b.absoluteWeight - a.absoluteWeight),
      itemsToDisplay: instrument.itemsToDisplay,
    }
  })

  return breakdownDataReduced
}

export function getETFHoldingsRefreshInterval() {
  const isPremium = FinvizSettings.hasUserPremium
  let customRefreshInterval = ETFHoldingsPollingIntervalInMs.Elite
  if (!isPremium) {
    customRefreshInterval = ETFHoldingsPollingIntervalInMs.Free
  } else if (!isStockFastRefreshAvailable()) {
    customRefreshInterval = ETFHoldingsPollingIntervalInMs.EliteStocksReduced
  }

  return customRefreshInterval
}
